import { useEffect, useMemo } from 'react';
import FilterPanel, {
  type FilterPanelBundle,
} from '../FilterPanel';
import {
  useDashboardIndices,
  useDashboardStore,
} from '../../state/dashboardStore';
import type { FilterStage } from '../../state/filterStore';

type Props = {
  dashboardId: string;
  open: boolean;
  onClose: () => void;
};

export default function DashboardFilterDrawer({
  dashboardId,
  open,
  onClose,
}: Props) {
  const dashboard = useDashboardStore((s) =>
    s.dashboards.find((d) => d.id === dashboardId) ?? null,
  );
  const updateFilterStages = useDashboardStore((s) => s.updateFilterStages);
  const filtered = useDashboardIndices(dashboardId);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const bundle = useMemo<FilterPanelBundle>(() => {
    const stages = dashboard?.filterStages ?? [];
    const currentStages = (): FilterStage[] => {
      const d = useDashboardStore
        .getState()
        .dashboards.find((x) => x.id === dashboardId);
      return d?.filterStages ?? [];
    };
    const selectionMode: 'all' | 'selection' =
      dashboard?.baseSelection || dashboard?.polygon ? 'selection' : 'all';
    return {
      stages,
      addStage: (stage) => {
        updateFilterStages(dashboardId, [...currentStages(), stage]);
      },
      updateStage: (id, patch) => {
        const next = currentStages().map((s) =>
          s.id === id ? ({ ...s, ...patch } as FilterStage) : s,
        );
        updateFilterStages(dashboardId, next);
      },
      removeStage: (id) => {
        updateFilterStages(
          dashboardId,
          currentStages().filter((s) => s.id !== id),
        );
      },
      clearStages: () => updateFilterStages(dashboardId, []),
      filtered,
      selectionMode,
    };
  }, [dashboard, dashboardId, filtered, updateFilterStages]);

  if (!open || !dashboard) return null;

  return (
    <aside
      className="dashboard-filter-drawer"
      aria-label="Dashboard filters"
      role="dialog"
    >
      <header className="dashboard-filter-drawer__header">
        <h3 className="dashboard-filter-drawer__title">Dashboard filters</h3>
        <button
          type="button"
          className="dashboard-filter-drawer__close"
          onClick={onClose}
          aria-label="Close filter drawer"
        >
          ×
        </button>
      </header>
      <div className="dashboard-filter-drawer__body">
        <FilterPanel bundle={bundle} />
      </div>
    </aside>
  );
}
